import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import {
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Box,
} from '@mui/material';
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from 'use-places-autocomplete';
import { resolveNamedMask } from '../../utils/maskAddress';

function AddressBarComponent({
  name,
  question,
  onChange,
  onNext,
  values,
  setValidationState,
  registerValidation,
  inputRef,
  nextFieldRef,
  isMobile,
  onLocationResolved,
  layoutVariant = 'default',
  readOnly = false,
}) {
  const isAccountLayout = layoutVariant === 'account';

  // `usePlacesAutocomplete` only checks for `window.google.maps.places`
  // once, synchronously on mount, with no retry. On a direct/deep-linked
  // page load this component can mount before the async Google Maps
  // script (kicked off in index.js) has finished loading, leaving the
  // field permanently disabled until a remount (e.g. a refresh). Gate
  // `initOnMount` on our own poll so it retries once the script is ready.
  const [mapsScriptReady, setMapsScriptReady] = useState(
    () => !!window.google?.maps?.places
  );

  useEffect(() => {
    if (mapsScriptReady) return;
    const interval = setInterval(() => {
      if (window.google?.maps?.places) {
        clearInterval(interval);
        setMapsScriptReady(true);
      }
    }, 150);
    return () => clearInterval(interval);
  }, [mapsScriptReady]);

  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: { componentRestrictions: { country: 'za' } },
    initOnMount: mapsScriptReady,
  });

  const initialValue = values?.[name] || question.value || '';
  const [inputValue, setInputValue] = useState(initialValue);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const localInputRef = useRef(null);
  const fieldRef = inputRef || localInputRef;
  const wrapperRef = useRef(null);
  const mousedownRef = useRef(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  const dropdownActive = focused && status === 'OK';

  // The suggestion list is portalled to <body>, so it has to be positioned by
  // hand. Measuring once, shortly after focus, was not enough: autofocus fires
  // while the step is still staggering in (each field starts 30px low) and
  // while the page is still settling, so the captured offset was already stale
  // by the time the suggestions arrived — the list rendered a field's height
  // too low, below the next question. Track the field for as long as the list
  // is open, in viewport coordinates so a scroll mid-measurement can't skew it.
  const updateDropdownPosition = useCallback(() => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;

    setDropdownPosition((prev) =>
      prev &&
      prev.top === rect.bottom &&
      prev.left === rect.left &&
      prev.width === rect.width
        ? prev
        : { top: rect.bottom, left: rect.left, width: rect.width }
    );
  }, []);

  useLayoutEffect(() => {
    if (!dropdownActive) return undefined;

    let frame = requestAnimationFrame(function track() {
      updateDropdownPosition();
      frame = requestAnimationFrame(track);
    });

    return () => cancelAnimationFrame(frame);
  }, [dropdownActive, updateDropdownPosition]);

  // 'ZERO_RESULTS' (and '' before anything's typed) are normal, expected
  // states — everything else (OVER_QUERY_LIMIT, REQUEST_DENIED,
  // INVALID_REQUEST, UNKNOWN_ERROR) means the autocomplete is actually
  // broken (billing/quota/API key issue) and would otherwise look
  // indistinguishable from "no results" to anyone watching the UI.
  useEffect(() => {
    if (!status || status === 'OK' || status === 'ZERO_RESULTS') return;
    Sentry.captureException(new Error(`AddressBar autocomplete: ${status}`), {
      tags: { source: 'address-bar-autocomplete', status },
    });
  }, [status]);

  useEffect(() => {
    registerValidation?.(name, triggerValidation);
  }, [inputValue]);

  useEffect(() => {
    if (question.value && question.value !== inputValue) {
      setInputValue(question.value);
      setValue(question.value, false);
    }
  }, [question.value]);

  useEffect(() => {
    const handleMouseDown = () => (mousedownRef.current = true);
    const handleMouseUp = () => (mousedownRef.current = false);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Only the Next button (and Enter) validates loudly; typing validates
  // silently so we track validity without a message under a field in progress.
  const validate = (input = inputValue, { showError = true } = {}) => {
    const isValid = !!input;
    const failedRequired = !isValid && question.validation?.required;
    setError(
      showError && failedRequired
        ? question.validation.message || 'Required'
        : ''
    );
    setValidationState?.(name, isValid);
    return isValid;
  };
  const triggerValidation = () => validate();

  const handleClear = (e) => {
    e.preventDefault();
    setInputValue('');
    setValue('', false);
    clearSuggestions();
    setError('');
    onChange?.(name, '');
    if (inputRef?.current) inputRef.current.focus();
  };

  const handleInput = (e) => {
    const input = e.target.value;
    setInputValue(input);
    setValue(input);
    onChange?.(name, input);
    validate(input, { showError: false });
  };

  const handleFocus = () => {
    setFocused(true);
    setRevealed(true);

    // Reveal the real address inside the focus event, not on the re-render -
    // the same fix TextFieldComponent has. By the re-render the masked text
    // may already be selected (tabbing in does that, and so does Playwright's
    // fill()), and writing the new value collapses the selection to the end,
    // so typing over a prefilled address appended to it instead of replacing
    // it. Selecting the revealed value keeps a type-over a replacement.
    const input = fieldRef.current;
    const unmasked = inputValue || '';
    if (input && input.value !== unmasked) {
      input.value = unmasked;
      input.select();
    }
  };

  const handleBlur = () => {
    setRevealed(false);
    setTimeout(() => {
      if (!mousedownRef.current) setFocused(false);
    }, 0);
  };

  const handleSelect = async (address) => {
    setInputValue(address);
    setValue(address, false);
    clearSuggestions();
    onChange?.(name, address);
    validate(address, { showError: false });

    if (nextFieldRef?.current) nextFieldRef.current.focus();
    else onNext?.();

    setFocused(false);

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      onLocationResolved?.({ lat, lng }, address, results[0]);
    } catch (err) {
      // This was silently ignored — but getGeocode/getLatLng reject on
      // OVER_QUERY_LIMIT, REQUEST_DENIED, etc. (often just the bare status
      // string, not an Error), and a failure here means onLocationResolved
      // never fires, so anything depending on it (e.g. fibre availability
      // checks) silently stalls with no feedback to the user or to us.
      const reason = err instanceof Error ? err.message : String(err);
      Sentry.captureException(new Error(`AddressBar geocode: ${reason}`), {
        tags: { source: 'address-bar-geocode' },
        contexts: { addressBar: { address } },
      });
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (status === 'OK' && data.length > 0) {
        handleSelect(data[0].description);
      } else if (validate()) {
        if (nextFieldRef?.current) nextFieldRef.current.focus();
        else onNext?.();
      }
    }
  };

  const sizeOverrides = {
    'extra-small': '219px',
    small: '301px',
    medium: '403px',
    large: '534px',
    half: '500px',
    full: '1000px',
    'vehicle-return': '324px',
    'vehicle-return-full': '662px',
    'vehicle-full': '448px',
    'accounts-half': '416px',
    'accounts-full': '864px',
  };

  const fieldWidth = question.customSize
    ? sizeOverrides[question.customSize] || '100%'
    : '100%';
  const responsiveWidth = isAccountLayout
    ? '100%'
    : isMobile
      ? '80vw'
      : fieldWidth;

  // Addresses are prefilled from a credit bureau lookup against the user's ID,
  // so - like the cell number and email on the same journey - the value is
  // masked while the field is idle and only revealed once it's focused for
  // editing. `inputValue` stays the raw value throughout: the mask is display
  // only and never reaches onChange, validation or the suggestion lookup.
  // Unmasking is tracked separately from `focused` - that flag also drives the
  // suggestion dropdown and is cleared behind a mousedown guard, so it can
  // linger after the user has clicked away.
  const maskedValue = useMemo(() => {
    if (!inputValue || typeof question.mask !== 'string') return inputValue;
    try {
      const maskFunc =
        resolveNamedMask(question.mask) || new Function('input', question.mask);
      const masked = maskFunc(inputValue);
      return typeof masked === 'string' ? masked : inputValue;
    } catch (err) {
      return inputValue;
    }
  }, [inputValue, question.mask]);

  const displayValue = revealed ? inputValue : maskedValue;

  // Hide placeholder when focused or when a value exists (prevents label+placeholder clash)
  const placeholderText =
    focused || inputValue ? '' : question.placeholder || '';

  const dropdown =
    dropdownActive && dropdownPosition
      ? ReactDOM.createPortal(
          <Paper
            elevation={3}
            sx={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              borderRadius: '0 0 20px 20px',
              border: '1px solid #A2ACB1', // ← match border color
              borderTop: 'none',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
              overflowY: 'auto',
              maxHeight: '250px',
              zIndex: 2000,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              pointerEvents: 'auto',
              backgroundColor: '#fff',
            }}
          >
            <List>
              {data.map(({ place_id, description }) => (
                <ListItem key={place_id} disablePadding>
                  <ListItemButton onClick={() => handleSelect(description)}>
                    <ListItemText
                      primary={description}
                      primaryTypographyProps={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        color: '#162F3B', // value color
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>,
          document.body
        )
      : null;

  return (
    <Box
      sx={{
        width: responsiveWidth,
        maxWidth: '100%',
        mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
        boxSizing: 'border-box',
      }}
    >
      <Box
        ref={wrapperRef}
        sx={{
          position: 'relative',
          width: isAccountLayout ? '100%' : isMobile ? '80vw' : '100%',
          maxWidth: isAccountLayout ? '100%' : isMobile ? '100%' : fieldWidth,
          mx: isAccountLayout ? 'auto' : isMobile ? 'auto' : undefined,
        }}
      >
        <TextField
          fullWidth
          placeholder={placeholderText} // ← controlled placeholder
          label={question.label}
          value={displayValue}
          onChange={readOnly ? undefined : handleInput}
          onKeyDown={readOnly ? undefined : handleKeyDown}
          disabled={!readOnly && !ready}
          variant="outlined"
          error={!!error}
          helperText={error}
          inputRef={fieldRef}
          onFocus={readOnly ? undefined : handleFocus}
          onBlur={readOnly ? undefined : handleBlur}
          InputLabelProps={{
            sx: {
              color: '#738289', // label color
              // '&.Mui-focused': { color: '#738289' },
              // '&.Mui-error': { color: '#738289' },
            },
          }}
          InputProps={{
            readOnly,
            endAdornment: readOnly ? null : inputValue ? (
              <span
                className="material-symbols-rounded"
                onMouseDown={handleClear}
                style={{
                  fontWeight: 400,
                  fontSize: 20,
                  color: '#738289',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                close
              </span>
            ) : (
              <span
                className="material-symbols-rounded"
                style={{ fontWeight: 500, fontSize: 24, color: '#162F3B' }}
              >
                search
              </span>
            ),
          }}
          sx={{
            width: '100%',
            mx: isAccountLayout ? 'auto' : undefined,
            '& .MuiOutlinedInput-root': {
              borderRadius: dropdownActive ? '12px 12px 0 0' : '12px',
              paddingTop: '16px',
              paddingBottom: '16px',
              paddingLeft: '12px',
              paddingRight: '12px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '1rem',
              lineHeight: isMobile ? '100%' : '1.2rem',
              letterSpacing: '-0.02em',

              // Borders: default/hover/focus all #738289
              '& fieldset': {
                borderRadius: dropdownActive ? '12px 12px 0 0' : '12px',
                borderWidth: '1px',
                borderColor: '#A2ACB1',
              },
              // '&:hover fieldset': { borderColor: '#738289' },
              // '&.Mui-focused fieldset': { borderColor: '#738289' },
              // '&.Mui-error fieldset': { borderColor: '#738289' },

              '& input': {
                padding: 0,
                height: 'auto',
                boxSizing: 'border-box',
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                lineHeight: '1.2rem',
                color: inputValue ? '#162F3B' : '#738289', // value vs empty
                '&::placeholder': {
                  color: '#738289', // placeholder
                  opacity: 1,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '1rem',
                  lineHeight: '1.2rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              },
            },
            '& .MuiFormHelperText-root': {
              textAlign: 'right',
              fontSize: isMobile ? '0.6rem' : '0.75rem',
              color: '#D32F2F',
              fontFamily: 'Inter, sans-serif',
              mt: '0px',
              mx: 0,
              pr: '8px',
            },
          }}
        />
        {dropdown}
      </Box>
    </Box>
  );
}

export default AddressBarComponent;
